<template>
  <div class="md-layout">
    <div class="md-layout-item">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>people</md-icon>
          </div>
          <h4 class="title">Users List</h4>
        </md-card-header>
        <div
          style="width: 100%; display: flex; justify-content: flex-end; margin-right: 30px;"
        >
          <md-button
            :disabled="isSelectedUsersListEmpty"
            class="md-primary opacity-50"
            @click="toggleEmailsModalVisibility"
            style="margin-right: 20px;"
          >
            {{ $t("Send Emails") }}
          </md-button>
          <md-button
            :disabled="!isSelectedUsersListEmpty"
            class="md-primary opacity-50"
            @click="sendEmailsToAllThroughModal"
            style="margin-right: 20px;"
          >
            {{ $t("Send Emails to All") }}
          </md-button>
        </div>
        <md-card-content>
          <md-table
            md-card
            :value="userProfiles"
            :md-selected-value.sync="selectedUsers"
            class="paginated-table table-striped table-hover page-list-table"
            @md-selected="onUserSelect"
          >
            <md-table-toolbar>
              <md-field>
                <label for="pages">Per page</label>
                <md-select v-model="pagination.perPage" name="pages">
                  <md-option
                    v-for="(item, position) in pagination.perPageOptions"
                    :key="position"
                    :label="item"
                    :value="item"
                  >
                    {{ item }}
                  </md-option>
                </md-select>
              </md-field>

              <md-field>
                <md-input
                  type="search"
                  clearable
                  style="width: 200px"
                  :placeholder="$t('Search')"
                  v-model="searchQuery"
                />
              </md-field>
            </md-table-toolbar>
            <md-table-toolbar
              slot="md-table-alternate-header"
              slot-scope="{ count }"
              :value="selectedUsers"
            >
              <div class="md-toolbar-section-start">
                <span style="font-weight: bold; margin-right: 10px">
                  {{ `${count} user${count > 1 ? "s" : ""} selected` }}
                </span>
              </div>
            </md-table-toolbar>

            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }"
              md-selectable="multiple"
              md-auto-select
            >
              <md-table-cell :md-label="$t('Name')">
                {{ item.firstname + " " + item.lastname }}
              </md-table-cell>
              <md-table-cell :md-label="$t('Email')">
                {{ item.email }}
              </md-table-cell>
              <md-table-cell :md-label="$t('Type')">
                {{ item.usertype }}
              </md-table-cell>
              <md-table-cell :md-label="$t('Company')">
                {{ item.company }}
              </md-table-cell>
              <md-table-cell :md-label="$t('Newsletter')">
                {{ item.newsletter }}
              </md-table-cell>
              <md-table-cell :md-label="$t('Activated')">
                {{ item.activated }}
              </md-table-cell>
              <md-table-cell :md-label="$t('Registered On')">
                {{ item.registered_on }}
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
        <md-card-actions md-alignment="space-between">
          <div class>
            <p class="card-category">
              {{
                $t("Showing") +
                  " " +
                  (pagination.total === 0
                    ? 0
                    : (pagination.currentPage - 1) * pagination.perPage + 1) +
                  " " +
                  $t("to") +
                  " " +
                  (pagination.currentPage * pagination.perPage >
                  pagination.total
                    ? pagination.total
                    : pagination.currentPage * pagination.perPage) +
                  " " +
                  $t("of") +
                  " " +
                  pagination.total +
                  " " +
                  $t("entries")
              }}
            </p>
          </div>
          <pagination
            class="pagination-no-border"
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="pagination.total"
            @pageChange="onPagination"
          />
        </md-card-actions>
      </md-card>
    </div>

    <send-emails-modal
      v-if="showingSendEmailsModal"
      :user-email="email"
      :users="selectedUsers"
      :to-all="sendEmailsToAll"
      @closeModal="toggleEmailsModalVisibility"
    />
  </div>
</template>

<script>
import { Pagination } from "@/components";
import swal from "sweetalert2";
import { mapFields } from "vuex-map-fields";
import { router } from "@/main";
import SendEmailsModal from "@/pages/Dashboard/Admin/SendEmailsModal.vue";
import { getUserProfiles } from "@/services/auth.service";
import { debounce } from "@/helpers/utils";
import { dateOptions, USER_TYPE } from "@/const";

export default {
  components: {
    SendEmailsModal,
    Pagination
  },
  computed: {
    ...mapFields("auth", ["email", "usertype"]),
    isSelectedUsersListEmpty() {
      return this.selectedUsers.length === 0;
    }
  },
  data() {
    return {
      pagination: {
        perPage: 10,
        currentPage: 1,
        perPageOptions: [10, 25, 50, 100],
        total: 0
      },
      searchQuery: "",
      showingSendEmailsModal: false,
      sendEmailsToAll: false,
      selectedUsers: [],
      userProfiles: [],
      debouncedSearch: debounce(this.getUserProfilesInPage, 300)
    };
  },
  methods: {
    toggleEmailsModalVisibility() {
      this.showingSendEmailsModal = !this.showingSendEmailsModal;

      if (!this.showingSendEmailsModal) {
        this.selectedUsers = [];
        this.sendEmailsToAll = false;
      }
    },
    sendEmailsToAllThroughModal() {
      this.sendEmailsToAll = true;
      this.showingSendEmailsModal = true;
    },
    noAccessWarning() {
      swal
        .fire({
          title: "Warning",
          text: "Only administrator can access here!",
          type: "warning",
          confirmButtonClass: "md-button md-warning",
          buttonsStyling: false
        })
        .then(() => {
          router.push("/profile");
        });
    },
    async getUserProfilesInPage(currentPage = 1, perPage = 10, keywords) {
      const { data, total } = await getUserProfiles({
        current_page: currentPage,
        per_page: perPage,
        keywords
      });
      const userProfiles = JSON.parse(data);
      this.userProfiles = userProfiles.map(user => {
        let bricks = {
          newsletter: user.newsletter === undefined ? false : user.newsletter,
          company: user.company || "",
          registered_on_date: user.registered_on.$date,
          registered_on: new Date(user.registered_on.$date).toLocaleDateString(
            window.localStorage.getItem("locale") === "fr" ? "fr-CA" : "en-US",
            dateOptions
          ),
          plan_type: user.plan_type || "",
          plan_freq: user.plan_freq || ""
        };

        return { ...user, ...bricks };
      });

      this.pagination = {
        perPage,
        currentPage,
        perPageOptions: [10, 25, 50, 100],
        total
      };
    },
    onPagination(currentPage) {
      this.getUserProfilesInPage(
        currentPage,
        this.pagination.perPage,
        this.searchQuery
      );
    },
    onUserSelect(items) {
      this.selectedUsers = items;
    }
  },
  mounted() {
    let { currentPage, perPage } = this.pagination;
    this.getUserProfilesInPage(currentPage, perPage);
  },
  watch: {
    usertype(newValue, oldValue) {
      if (newValue && newValue !== oldValue && newValue !== USER_TYPE.ADMIN) {
        this.noAccessWarning();
      }
    },
    searchQuery(value) {
      let { perPage } = this.pagination;
      this.pagination.currentPage = 1;
      this.debouncedSearch(1, perPage, value);
    },
    "pagination.perPage": function(perPage) {
      this.pagination.currentPage = 1;
      this.getUserProfilesInPage(1, perPage, this.searchQuery);
    }
  }
};
</script>

<style lang="css">
.page-list-table .md-table-cell-container {
  padding: 0;
}
</style>
